import { useCallback, useEffect, useRef, useState } from 'react';
import WorkspaceKitIcon from '../jobseeker-v2/components/WorkspaceKitIcon';
import './shellKit.css';

/* Status icon per v2 section 03 — load-bearing now the left rule is gone. */
const STATUS_ICONS = {
  success: 'check-circle',
  warning: 'alert-triangle',
  error: 'x-circle',
  info: 'info',
};

const TOAST_DURATION_MS = 5000;
const MAX_VISIBLE_TOASTS = 4;

/**
 * Kit toast stack (RSW-601). Listens to the same `rsworks:notify` events the
 * notify() util has always dispatched — call sites are unchanged. Statuses:
 * info | success | warning | error. Auto-dismisses; always closable.
 */
export default function AppNotifications() {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  useEffect(() => {
    const timers = new Map();

    const onNotify = (event) => {
      const detail = event.detail || {};
      idRef.current += 1;
      const id = idRef.current;
      const toast = {
        id,
        title: detail.title || 'Notice',
        description: detail.description || '',
        status: ['info', 'success', 'warning', 'error'].includes(detail.status)
          ? detail.status
          : 'info',
      };
      setToasts((current) => [...current, toast].slice(-MAX_VISIBLE_TOASTS));
      timers.set(id, setTimeout(() => {
        timers.delete(id);
        dismiss(id);
      }, TOAST_DURATION_MS));
    };

    window.addEventListener('rsworks:notify', onNotify);
    return () => {
      window.removeEventListener('rsworks:notify', onNotify);
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, [dismiss]);

  // The live region stays mounted even when empty: many screen-reader/browser
  // combinations only announce changes inside a region that existed beforehand.
  return (
    <div className="rs-toasts" role="region" aria-label="Notifications" aria-live="polite">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`rs-toast rs-toast--${toast.status}`}
          role={toast.status === 'error' ? 'alert' : 'status'}
        >
          <span className="rs-toast__icon" aria-hidden>
            <WorkspaceKitIcon name={STATUS_ICONS[toast.status] || 'info'} size={18} />
          </span>
          <div className="rs-toast__body">
            <p className="rs-toast__title">{toast.title}</p>
            {toast.description ? (
              <p className="rs-toast__desc">{toast.description}</p>
            ) : null}
          </div>
          <button
            type="button"
            className="rs-toast__close"
            aria-label="Dismiss notification"
            onClick={() => dismiss(toast.id)}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
